import { getEnv } from '@xbcb/ui-utils';
import { UiStage, uiStageToBackendStage } from '@xbcb/ui-types';
import {
  RENEWAL_DATE_FEATURE,
  renewalDateFeature,
  cbpReg2ChangesFeature,
  CBP_REG_2_CHANGES_FEATURE,
  AP_INTEGRATION_FEATURE,
  apIntegrationFeature,
  PROJECT42_FEATURE,
  Project42Features,
  VAULT_P0_FEATURE,
  FeatureFlagContext,
  UI_LOCALIZATION_FEATURE,
  uiLocalizationFeatureName,
  RESET_PASSWORD_FOR_EXT_CUSTOMERS_FEATURE,
  resetPasswordForExtCustomersFeatureName,
  ESCALATION_MESSAGE_SEARCH_FEATURE,
  escalationMessageSearchFeatureName,
  INVOICE_BULK_DOWNLOAD_FEATURE,
  SUBSCRIPTIONS_DISABLED_FEATURE,
  subscriptionsDisabledFeatureName,
  CONTINUOUS_BOND_AUTOMATION_FEATURE,
  continuousBondAutomationFeature,
  PATHFINDER_FEATURE_FLAG_SERVICE,
  PathfinderFeatures,
  EU_UK_AR_INTEGRATION_FEATURE,
  euUkArIntegrationFeature,
  WORK_ORDER_CLONING_FEATURE,
  workOrderCloningFeatureName,
  ExternalUserMigrationFeatures,
  ASSIGNMENT_WORKSPACE_FEATURE,
  EXTERNAL_USER_MIGRATION_FEATURE_FLAG_SERVICE,
  ASSIST_FEATURE,
  assistFeature,
  CBAM_DETECTION_FEATURE,
  cbamDetectionFeature,
  CONNECT_PRO_FEATURE,
  DELIVERY_ORDER_AUTOMATION_FEATURE,
  deliveryOrderAutomationFeature,
  DELOREAN_FEATURE_FLAG_SERVICE,
  DeloreanFeatures,
  FFCD_CUSTOMS_MILESTONES_FEATURE,
  FfcdCustomsMilestonesFeature,
  GLITCHWATCH_FEATURE,
  GlitchWatchFeatures,
  HYDRA_FEATURE,
  hydraFeature,
  HYDRA_LINE_ITEM_VALIDATION_FEATURE,
  hydraLineItemValidationFeature,
  SSO,
  ssoFeature,
  US_HTS_UI_MANAGEMENT_FEATURE,
  usHtsUiManagementFeatureName,
  REGULATORY_CONTACT_FEATURE,
  idpFeature,
  IDP_FEATURE,
  CPSC_FEATURE,
  FCC_READY_DATE_FEATURE,
  PGA_DISCLAIM_FEATURE,
  PgaDisclaimFeature,
  HTS_REASONABILITY_FEATURE,
  HtsReasonabilityFeature,
  SECTION_232_COPPER_FEATURE,
  section232CopperFeature,
} from '@xbcb/feature-flags';
import { CurrentUser } from 'libs/hooks';
import { AccountType, WorkOrderRecordType } from '@xbcb/shared-types';
import { canAccessStatementReauthorization } from 'libs/statementReauthorizationConditions';

import { Tag } from '@xbcb/api-gateway-client';
import log from '@xbcb/log';

const { stage } = getEnv();

export const isReleaseNoteViewEnabled = stage !== UiStage.PROD;

// Shipper~Importer relationships card on the Importer / IOR activation views.
// Non-prod only until ready for prod rollout.
export const isShipperRelationshipsEnabled = stage !== UiStage.PROD;

export const isCuDeCommercialInvoiceTabEnabled = stage !== UiStage.PROD;

export const isCommodityGroupViewEnabled = true;

// Enabling Deep Thought HTS code validation feature only for LOCAL, ALPHA, and BETA
// Currently scoped to US Consumption Entry and Post Summary Correction only
const DEEP_THOUGHT_ENABLED_STAGES = [
  UiStage.LOCAL,
  UiStage.ALPHA,
  UiStage.BETA,
  UiStage.GAMMA,
  UiStage.PROD,
];
const DEEP_THOUGHT_SUPPORTED_WORK_ORDER_TYPES = [
  WorkOrderRecordType.US_CONSUMPTION_ENTRY,
  WorkOrderRecordType.US_POST_SUMMARY_CORRECTION,
];

export const isDeepThoughtClassificationEnabled = (
  workOrderType?: WorkOrderRecordType,
): boolean =>
  DEEP_THOUGHT_ENABLED_STAGES.includes(stage) &&
  DEEP_THOUGHT_SUPPORTED_WORK_ORDER_TYPES.includes(
    workOrderType as WorkOrderRecordType,
  );

export const isPgaDisclaimFeatureEnabled = PGA_DISCLAIM_FEATURE.isEnabled(
  PgaDisclaimFeature,
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const isHtsReasonabilityEnabled = HTS_REASONABILITY_FEATURE.isEnabled(
  HtsReasonabilityFeature,
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const isRegulatoryContactEnabled = REGULATORY_CONTACT_FEATURE.isEnabled(
  'REGULATORY_CONTACT_FEATURE',
  {
    stage: uiStageToBackendStage[stage],
  },
);
export const isUsHtsUiManagementEnabled =
  US_HTS_UI_MANAGEMENT_FEATURE.isEnabled(usHtsUiManagementFeatureName, {
    stage: uiStageToBackendStage[stage],
  });

export const isSSOEnabled = SSO.isEnabled(ssoFeature, {
  stage: uiStageToBackendStage[stage],
});

export const isHydraFeatureEnabled = HYDRA_FEATURE.isEnabled(hydraFeature, {
  stage: uiStageToBackendStage[stage],
});

export const isHydraLineItemValidationEnabled =
  HYDRA_LINE_ITEM_VALIDATION_FEATURE.isEnabled(hydraLineItemValidationFeature, {
    stage: uiStageToBackendStage[stage],
  });

export const isGlitchWatchUiAdminViewEnabled = (user: CurrentUser): boolean => {
  if (user.accountType !== AccountType.OPERATOR) return false;
  return TechTeam.isMember(user);
};

class TechTeam {
  private static GAMMA_TEAM_IDS = [
    'businessSupportTeam_06ad7389-85d0-45c6-914f-01c82c2d969d',
    'businessSupportTeam_88336167-d9af-47a3-8de6-6d3415a48a18',
    'businessSupportTeam_3a4c9291-215c-4b63-8a73-44352f20f81d',
  ];

  private static PROD_TEAM_IDS = [
    'businessSupportTeam_30c0058a-e5d1-4609-a3f9-05115b6d1b24',
    'businessSupportTeam_a39dc3ac-9196-4ffd-b0a3-92657f475b4f',
    'businessSupportTeam_ca5a888c-367f-43f1-98f3-d0a42686aeb5',
  ];

  private static TEAM_IDS = new Set([
    ...TechTeam.GAMMA_TEAM_IDS,
    ...TechTeam.PROD_TEAM_IDS,
  ]);

  static isMember({ businessSupportTeams }: CurrentUser): boolean {
    for (const node of businessSupportTeams?.nodes ?? []) {
      if (TechTeam.TEAM_IDS.has(node.id)) {
        return true;
      }
    }
    return false;
  }
}

export const isGlitchWatchUiAlertButtonEnabled = GLITCHWATCH_FEATURE.isEnabled(
  GlitchWatchFeatures.UI_ALERT_BUTTON,
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const isFfcdCustomsMilestonesEnabled =
  FFCD_CUSTOMS_MILESTONES_FEATURE.isEnabled(FfcdCustomsMilestonesFeature, {
    stage: uiStageToBackendStage[stage],
  });
export const computeIsDeloreanBasicPhaseEnabled = () => {
  const currentDate = new Date();
  return DELOREAN_FEATURE_FLAG_SERVICE.isEnabled(DeloreanFeatures.BASIC, {
    stage: uiStageToBackendStage[getEnv().stage],
    time: currentDate.toISOString().slice(0, 10),
  });
};

export const computeIsDeloreanMainPhaseEnabled = () => {
  const currentDate = new Date();
  return DELOREAN_FEATURE_FLAG_SERVICE.isEnabled(DeloreanFeatures.MAIN, {
    stage: uiStageToBackendStage[getEnv().stage],
    time: currentDate.toISOString(),
  });
};

export const computeIsFrComfortPhaseEnabled = () => {
  const currentDate = new Date();
  return DELOREAN_FEATURE_FLAG_SERVICE.isEnabled(DeloreanFeatures.FR_COMFORT, {
    stage: uiStageToBackendStage[getEnv().stage],
    time: currentDate.toISOString(),
  });
};

export const computeIsDeComfortPhaseEnabled = () => {
  const currentDate = new Date();
  return DELOREAN_FEATURE_FLAG_SERVICE.isEnabled(DeloreanFeatures.DE_COMFORT, {
    stage: uiStageToBackendStage[getEnv().stage],
    time: currentDate.toISOString(),
  });
};

export const isDeloreanBasicPhaseEnabled = computeIsDeloreanBasicPhaseEnabled();
export const isDeloreanMainPhaseEnabled = computeIsDeloreanMainPhaseEnabled();
export const isFrComfortPhaseEnabled = computeIsFrComfortPhaseEnabled();
export const isDeComfortPhaseEnabled = computeIsDeComfortPhaseEnabled();

export const isDeliveryOrderAutomationEnabled =
  DELIVERY_ORDER_AUTOMATION_FEATURE.isEnabled(deliveryOrderAutomationFeature, {
    stage: uiStageToBackendStage[stage],
  });

export const isConnectProEnabled = CONNECT_PRO_FEATURE.isEnabled(
  'CONNECT_PRO_FEATURE',
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const isAssistEnabled = ASSIST_FEATURE.isEnabled(assistFeature, {
  stage: uiStageToBackendStage[stage],
});

export const isWorkOrderCloningEnabled = WORK_ORDER_CLONING_FEATURE.isEnabled(
  workOrderCloningFeatureName,
  {
    stage: uiStageToBackendStage[stage],
  },
);
export const isEuUkArIntegrationEnabled =
  EU_UK_AR_INTEGRATION_FEATURE.isEnabled(euUkArIntegrationFeature, {
    stage: uiStageToBackendStage[stage],
  });
export const PATHFINDER_PHASE_3_ENABLED =
  PATHFINDER_FEATURE_FLAG_SERVICE.isEnabled(
    PathfinderFeatures.PHASE_3,

    { stage: uiStageToBackendStage[stage] },
  );
export const PATHFINDER_PHASE_2_ENABLED =
  PATHFINDER_FEATURE_FLAG_SERVICE.isEnabled(
    PathfinderFeatures.PHASE_2,

    { stage: uiStageToBackendStage[stage] },
  );
export const PATHFINDER_PHASE_1_ENABLED =
  PATHFINDER_FEATURE_FLAG_SERVICE.isEnabled(
    PathfinderFeatures.PHASE_1,

    { stage: uiStageToBackendStage[stage] },
  );
export const isContinuousBondAutomationEnabled =
  CONTINUOUS_BOND_AUTOMATION_FEATURE.isEnabled(
    continuousBondAutomationFeature,
    { stage: uiStageToBackendStage[stage] },
  );

export const isSubscriptionsDisabled = SUBSCRIPTIONS_DISABLED_FEATURE.isEnabled(
  subscriptionsDisabledFeatureName,
  {
    stage: uiStageToBackendStage[stage],
    time: new Date().toISOString(),
  },
);
export const isInvoiceBulkDownloadEnabled =
  INVOICE_BULK_DOWNLOAD_FEATURE.isEnabled('INVOICE_BULK_DOWNLOAD_FEATURE', {
    stage: uiStageToBackendStage[stage],
  });

export const isStatementReauthorizationEnabled = (
  user?: CurrentUser,
): boolean => {
  if (!user) return false;
  return canAccessStatementReauthorization(user);
};

export const isEscalationMessageSearchEnabled =
  ESCALATION_MESSAGE_SEARCH_FEATURE.isEnabled(
    escalationMessageSearchFeatureName,
    { stage: uiStageToBackendStage[stage] },
  );
export const isUiLocalizationEnabled = UI_LOCALIZATION_FEATURE.isEnabled(
  uiLocalizationFeatureName,
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const iscbpReg2ChangesFeatureEnabled =
  CBP_REG_2_CHANGES_FEATURE.isEnabled(cbpReg2ChangesFeature, {
    stage: uiStageToBackendStage[stage],
  });

export const isRenewalDateEnabled: boolean = RENEWAL_DATE_FEATURE.isEnabled(
  renewalDateFeature,
  {
    stage: uiStageToBackendStage[stage],
  },
);
export const isSubscriptionsFeatureDisabled = (props: {
  stage?: UiStage;
  time?: string;
}): boolean => {
  // TODO: remove operator from context
  const context: FeatureFlagContext = {
    stage: uiStageToBackendStage[props.stage ?? getEnv().stage],
    time: props.time,
  };
  return SUBSCRIPTIONS_DISABLED_FEATURE.isEnabled(
    subscriptionsDisabledFeatureName,
    context,
  );
};

export const isExternalUserBannerEnabled = (): boolean => {
  return EXTERNAL_USER_MIGRATION_FEATURE_FLAG_SERVICE.isEnabled(
    ExternalUserMigrationFeatures.MIGRATION_BANNER,
    {
      stage: uiStageToBackendStage[stage],
      time: new Date().toISOString(),
    },
  );
};

export const isExternalUserForcedMigrationEnabled = (): boolean => {
  return EXTERNAL_USER_MIGRATION_FEATURE_FLAG_SERVICE.isEnabled(
    ExternalUserMigrationFeatures.FORCED_MIGRATION,
    {
      stage: uiStageToBackendStage[stage],
      time: new Date().toISOString(),
    },
  );
};

export const checkResetPasswordPermission = (
  operatorUserId?: string,
): boolean => {
  return (
    operatorUserId != null &&
    RESET_PASSWORD_FOR_EXT_CUSTOMERS_FEATURE.isEnabled(
      resetPasswordForExtCustomersFeatureName,
      {
        stage: uiStageToBackendStage[getEnv().stage],
        operator: operatorUserId,
      },
    )
  );
};
export const isAssignmentWorkspaceFeatureEnabled = (props: {
  stage?: UiStage;
  operator?: string;
}): boolean => {
  // TODO: remove operator from context
  const context: FeatureFlagContext = {
    stage: uiStageToBackendStage[props.stage ?? getEnv().stage],
    operator: props.operator,
  };
  return ASSIGNMENT_WORKSPACE_FEATURE.isEnabled(
    'ASSIGNMENT_WORKSPACE_FEATURE',
    context,
  );
};

// Convert the launch time string to a Date object
const AP_LAUNCH_TIME = new Date('2024-11-15T00:00:00.000Z');

export const isApIntegrationEnabled = ({
  tags,
  time,
}: {
  tags?: Tag[];
  time: string;
}): boolean => {
  const isFeatureEnabled = AP_INTEGRATION_FEATURE.isEnabled(
    apIntegrationFeature,
    { stage: uiStageToBackendStage[getEnv().stage] },
  );

  if (stage === UiStage.PROD) {
    log.info(
      `Checking if AP need to be enabled, launch time: ${AP_LAUNCH_TIME} processTime: ${time}`,
      {
        key: 'isApIntegrationEnabled',
        params: { tags },
      },
    );
    if (isAfterLaunchTime(time)) {
      return isFeatureEnabled;
    }

    // Before the launch time, check if the AP_ENABLED tag is present
    return isFeatureEnabled && hasApEnabledTag(tags);
  }

  return isFeatureEnabled;
};

const isAfterLaunchTime = (time: string): boolean => {
  const processTime = new Date(time);
  return processTime >= AP_LAUNCH_TIME;
};

// Check if the AP_ENABLED tag is present
const hasApEnabledTag = (tags?: Tag[]): boolean => {
  return tags?.some((tag) => tag.key === 'AP_ENABLED') ?? false;
};

export const isProject42Enabled = (props: {
  stage?: UiStage;
  shipperId?: string;
}): boolean => {
  if (!props.shipperId) {
    return false;
  }

  const context: FeatureFlagContext = {
    stage: uiStageToBackendStage[props.stage ?? getEnv().stage],
    shipper: props.shipperId,
  };

  return PROJECT42_FEATURE.isEnabled(
    Project42Features.PROJECT42_USE_MOCK_DATA,
    context,
  );
};

export const isVaultP0Enabled = VAULT_P0_FEATURE.isEnabled('VAULT_P0_FEATURE', {
  stage: uiStageToBackendStage[stage],
});

export const isIdpFeatureEnabled = IDP_FEATURE.isEnabled(idpFeature, {
  stage: uiStageToBackendStage[stage],
});

export const isCpscFeatureEnabled = (props: {
  stage?: UiStage;
  time?: string;
  shipper?: string;
}): boolean => {
  const context: FeatureFlagContext = {
    stage: uiStageToBackendStage[props.stage ?? getEnv().stage],
    time: props.time,
    shipper: props.shipper,
  };

  return CPSC_FEATURE.isEnabled('CPSC_FEATURE', context);
};

export const isFccReadyDateEnabled = FCC_READY_DATE_FEATURE.isEnabled(
  'FCC_READY_DATE_FEATURE',
  {
    stage: uiStageToBackendStage[stage],
  },
);

// mDDP (Multi-vendor DDP) feature - enabled only in non-prod stages
export const isMddpFeatureEnabled = stage !== UiStage.PROD;

export const isTariffRedesignEnabled = true;

// Section 232 tariff automation - product summary fields (cartons, gross weight, net weight, volume)
// and net weight sync with Total Net Weight reporting field.
// Non-prod only until ready for gated rollout.
export const isTariff232AutomationFeatureEnabled =
  stage !== UiStage.PROD &&
  localStorage.getItem('tariff232AutomationFeature') === 'true';

export const isSection232CopperEnabled = SECTION_232_COPPER_FEATURE.isEnabled(
  section232CopperFeature,
  {
    stage: uiStageToBackendStage[stage],
  },
);

export const isCbamDetectionEnabled = CBAM_DETECTION_FEATURE.isEnabled(
  cbamDetectionFeature,
  { stage: uiStageToBackendStage[stage] },
);
